import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatearFechaHora } from '@cmms/shared';
import { api, ErrorApi } from '../servicios/api.js';
import { Aviso, CodigoQr, Dialogo, Etiqueta, Indicador } from './piezas.js';
import { IconoMas } from './iconos.js';
import { useEsMovil } from '../servicios/responsive.js';

interface TabletDeCuenta {
  tabletId: string;
  nombreDispositivo: string | null;
  cuenta: string;
  sede: string | null;
  emparejamientos: number;
  activa: boolean;
  personaActiva: string | null;
  ultimoUso: string | null;
  ultimoEmparejamiento: string;
}

/** Rutas de la API del detalle de tablets. La administración y la propia cuenta de sede tienen las suyas. */
export interface RutasTablets {
  listar: string;
  generar: string;
  /** Solo la administración ve el historial de cada tablet. */
  historial?: (tabletId: string) => string;
}

export const RUTAS_ADMIN_CUENTA = (usuarioId: string): RutasTablets => ({
  listar: `/usuarios/${usuarioId}/tablets`,
  generar: `/usuarios/${usuarioId}/emparejamientos`,
  historial: (tabletId) => `/dispositivos/tablets/${tabletId}/historial`,
});

export const RUTAS_MIS_TABLETS: RutasTablets = {
  listar: '/mis-tablets',
  generar: '/mis-tablets/emparejamientos',
};

/**
 * Tablets de una cuenta de sede: indicadores, QR de emparejamiento de un solo uso y la lista de
 * tablets. Para la cuenta de sede la lista muestra quién tiene asignada cada tablet; el historial
 * solo lo ve la administración. En celular la lista es de tarjetas; en escritorio, tabla.
 */
export function TabletsDeCuenta({
  rutas,
  clave,
  esAdmin,
}: {
  rutas: RutasTablets;
  clave: string;
  esAdmin: boolean;
}): JSX.Element {
  const esMovil = useEsMovil();
  const [qr, setQr] = useState<{ codigo: string; expiraEl: string } | null>(null);
  const [historialDe, setHistorialDe] = useState<TabletDeCuenta | null>(null);

  const { data: tablets, error } = useQuery({
    queryKey: ['tablets-cuenta', clave],
    queryFn: () => api.get<TabletDeCuenta[]>(rutas.listar),
    // Mientras el QR está en pantalla, la lista se actualiza sola para ver la tablet recién emparejada.
    refetchInterval: qr ? 4000 : false,
  });

  const generar = useMutation({
    mutationFn: () => api.post<{ codigo: string; expiraEl: string }>(rutas.generar, {}),
    onSuccess: (nuevo) => setQr(nuevo),
  });

  const mensajeError = error instanceof ErrorApi ? error.message : null;
  const lista = tablets ?? [];
  const asignadas = lista.filter((t) => t.activa).length;
  const ultimoUso = lista
    .map((t) => t.ultimoUso)
    .filter((v): v is string => v !== null)
    .sort()
    .at(-1);

  return (
    <>
      {mensajeError ? <Aviso tono="atencion">{mensajeError}</Aviso> : null}
      {generar.error ? (
        <Aviso tono="atencion">{generar.error instanceof ErrorApi ? generar.error.message : 'No se pudo generar el QR.'}</Aviso>
      ) : null}

      <div className="rejilla-indicadores">
        <Indicador titulo="TABLETS" valor={lista.length} tono="azul" />
        <Indicador titulo="ASIGNADAS" valor={asignadas} tono="verde" />
        <Indicador titulo="LIBRES" valor={lista.length - asignadas} tono="gris" />
        <Indicador
          titulo="ÚLTIMO USO"
          valor={ultimoUso ? formatearFechaHora(ultimoUso) : '—'}
          tono="ambar"
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {qr ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <CodigoQr codigo={qr.codigo} tamano={200} />
            <p className="campo-nota" style={{ textAlign: 'center' }}>
              Escanéelo desde la tablet, en la app de Continnua, opción «Reportar». Vence a las{' '}
              {formatearFechaHora(qr.expiraEl)} y solo sirve una vez.
            </p>
          </div>
        ) : null}

        {esMovil ? (
          <button
            type="button"
            className="boton-flotante-crear"
            aria-label={qr ? 'Generar otro QR' : 'Generar QR de emparejamiento'}
            title={qr ? 'Generar otro QR' : 'Generar QR de emparejamiento'}
            onClick={() => generar.mutate()}
            disabled={generar.isPending}
          >
            <IconoMas />
          </button>
        ) : (
          <div>
            <button type="button" className="boton" onClick={() => generar.mutate()} disabled={generar.isPending}>
              {generar.isPending ? 'Generando…' : qr ? 'Generar otro QR' : 'Generar QR de emparejamiento'}
            </button>
          </div>
        )}

        <h3 style={{ margin: 0 }}>Tablets de esta cuenta</h3>
        {tablets === undefined ? null : lista.length === 0 ? (
          <p className="campo-nota">Esta cuenta todavía no ha emparejado ninguna tablet.</p>
        ) : esMovil ? (
          <div className="tarjetas-ot">
            {lista.map((t) => (
              <div key={t.tabletId} className="tarjeta-ot">
                <div className="tarjeta-ot-cuerpo">
                  <div className="tarjeta-ot-cabecera">
                    <div className="tarjeta-ot-titulo">
                      {t.nombreDispositivo ?? 'Tablet'}{' '}
                      <code className="campo-nota" title={t.tabletId}>{t.tabletId.slice(0, 8)}</code>
                    </div>
                    <Etiqueta tono={t.activa ? 'verde' : 'gris'}>{t.activa ? 'Asignada' : 'Libre'}</Etiqueta>
                  </div>
                  <div className="tarjeta-ot-datos">
                    <span>{t.activa && t.personaActiva ? `Asignada a ${t.personaActiva}` : 'Sin asignar'}</span>
                    <span>{t.ultimoUso ? formatearFechaHora(t.ultimoUso) : 'Sin uso'}</span>
                  </div>
                  {esAdmin ? (
                    <button type="button" className="boton secundario pequeno" onClick={() => setHistorialDe(t)}>
                      Historial
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tabla">
              <thead>
                <tr>
                  <th>Tablet</th>
                  <th>ID</th>
                  <th>Asignada a</th>
                  <th>Estado</th>
                  <th>Último uso</th>
                  {esAdmin ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {lista.map((t) => (
                  <tr key={t.tabletId}>
                    <td>{t.nombreDispositivo ?? 'Tablet'}</td>
                    <td><code title={t.tabletId}>{t.tabletId.slice(0, 8)}</code></td>
                    <td>{t.activa && t.personaActiva ? t.personaActiva : 'Sin asignar'}</td>
                    <td>{t.activa ? 'Asignada' : 'Libre'}</td>
                    <td>{t.ultimoUso ? formatearFechaHora(t.ultimoUso) : '—'}</td>
                    {esAdmin ? (
                      <td>
                        <div className="acciones-fila">
                          <button type="button" className="boton secundario pequeno" onClick={() => setHistorialDe(t)}>
                            Historial
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {esAdmin && historialDe && rutas.historial ? (
        <DialogoHistorialTablet
          ruta={rutas.historial(historialDe.tabletId)}
          clave={historialDe.tabletId}
          nombre={historialDe.nombreDispositivo}
          alCerrar={() => setHistorialDe(null)}
        />
      ) : null}
    </>
  );
}

/** Detalle de una cuenta de sede, como diálogo, para la administración. */
export function DialogoTabletsSede({
  usuario,
  alCerrar,
}: {
  usuario: { id: string; nombreCompleto: string };
  alCerrar: () => void;
}): JSX.Element {
  return (
    <Dialogo titulo={`Cuenta · ${usuario.nombreCompleto}`} alCerrar={alCerrar} ancho={800}>
      <TabletsDeCuenta rutas={RUTAS_ADMIN_CUENTA(usuario.id)} clave={usuario.id} esAdmin />
    </Dialogo>
  );
}

interface EmparejamientoHistorial {
  id: string;
  etiqueta: string;
  nombrePersona: string | null;
  cuenta: string;
  sede: string | null;
  qrCreadoEl: string | null;
  qrCreadoPor: string | null;
  emparejadoEl: string;
  desvinculadoEl: string | null;
  activo: boolean;
  solicitudes: number;
}

interface HistorialTablet {
  tabletId: string;
  nombreDispositivo: string | null;
  emparejamientos: EmparejamientoHistorial[];
}

/**
 * Historial de una tablet, solo para administración: cada vez que se emparejó, con quién, cuándo se
 * generó su QR y cuántas solicitudes envió. El nombre de una persona solo aparece mientras su
 * emparejamiento está activo.
 */
export function DialogoHistorialTablet({
  ruta,
  clave,
  nombre,
  alCerrar,
}: {
  ruta: string;
  clave: string;
  nombre: string | null;
  alCerrar: () => void;
}): JSX.Element {
  const esMovil = useEsMovil();
  const { data, error, isLoading } = useQuery({
    queryKey: ['tablet-historial', clave],
    queryFn: () => api.get<HistorialTablet>(ruta),
  });

  const emparejamientos = data?.emparejamientos ?? [];
  const activos = emparejamientos.filter((e) => e.activo).length;
  const solicitudes = emparejamientos.reduce((suma, e) => suma + e.solicitudes, 0);

  return (
    <Dialogo titulo={`Historial · ${nombre ?? 'Tablet'} · ${clave.slice(0, 8)}`} alCerrar={alCerrar} ancho={900}>
      {error instanceof ErrorApi ? <Aviso tono="atencion">{error.message}</Aviso> : null}
      {isLoading ? <p className="campo-nota">Cargando…</p> : null}
      {data ? (
        <>
          <div className="rejilla-indicadores">
            <Indicador titulo="PERSONAS" valor={emparejamientos.length} tono="azul" />
            <Indicador titulo="ACTIVA" valor={activos} tono="verde" />
            <Indicador titulo="SOLICITUDES" valor={solicitudes} tono="ambar" />
          </div>

          {esMovil ? (
            <div className="tarjetas-ot">
              {emparejamientos.map((e) => (
                <div key={e.id} className="tarjeta-ot">
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <div className="tarjeta-ot-titulo">{e.nombrePersona ?? e.etiqueta}</div>
                      <Etiqueta tono={e.activo ? 'verde' : 'gris'}>{e.activo ? 'Activa' : 'Desvinculada'}</Etiqueta>
                    </div>
                    <div className="tarjeta-ot-datos">
                      <span>Cuenta: {e.cuenta}</span>
                      <span>Sede: {e.sede ?? '—'}</span>
                      <span>QR creado: {e.qrCreadoEl ? formatearFechaHora(e.qrCreadoEl) : '—'}</span>
                      <span>QR creado por: {e.qrCreadoPor ?? '—'}</span>
                      <span>Emparejada: {formatearFechaHora(e.emparejadoEl)}</span>
                      <span>Desvinculada: {e.desvinculadoEl ? formatearFechaHora(e.desvinculadoEl) : '—'}</span>
                      <span>Solicitudes: {e.solicitudes}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th>Cuenta</th>
                    <th>Sede</th>
                    <th>QR creado</th>
                    <th>QR creado por</th>
                    <th>Emparejada</th>
                    <th>Desvinculada</th>
                    <th>Solicitudes</th>
                  </tr>
                </thead>
                <tbody>
                  {emparejamientos.map((e) => (
                    <tr key={e.id}>
                      <td>
                        {e.nombrePersona ?? e.etiqueta}
                        {e.activo ? <span className="campo-nota"> · activa</span> : null}
                      </td>
                      <td>{e.cuenta}</td>
                      <td>{e.sede ?? '—'}</td>
                      <td>{e.qrCreadoEl ? formatearFechaHora(e.qrCreadoEl) : '—'}</td>
                      <td>{e.qrCreadoPor ?? '—'}</td>
                      <td>{formatearFechaHora(e.emparejadoEl)}</td>
                      <td>{e.desvinculadoEl ? formatearFechaHora(e.desvinculadoEl) : 'Activa'}</td>
                      <td>{e.solicitudes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </Dialogo>
  );
}
